import clsx from "clsx";
import { memo, use, useCallback, useEffect, useMemo } from "react";
import { preload } from "react-dom";
import { toast } from "sonner";
import { match } from "ts-pattern";

import { useGame } from "~/components/GameContext";
import { useTheme } from "~/hooks/theme";

import { type GameMode } from "../const";
import type { GameWord } from "../Game";
import { GameClue } from "../GameClue";
import { GamePlayContext } from "../GamePlayContext";
import type { GameAnswer } from "../utils/answer";
import Choices from "./Choices";
import { Classic } from "./Classic";

const QuestionAnswer: React.FC<{
  word: GameWord;
  mode: Exclude<GameMode, "pairs">;
  isDisabled: boolean;
}> = ({ word, mode, isDisabled }) => {
  const { game } = useGame();
  const { answers, addAnswer, addRewardTime, blastConfetti, endGame } =
    use(GamePlayContext);

  useEffect(() => {
    const nextImage = game.words[answers.length + 1]?.definitions.find(
      (definition) => definition.image,
    )?.image;
    if (nextImage) {
      // preload the next image, if there is one
      preload(nextImage, { as: "image" });
    }
  }, [game.words, answers.length]);

  const handleAnswer = useCallback(
    (answer: GameAnswer) => {
      if (answers.length === game.words.length - 1) {
        // if this is the last word, end the game
        endGame(answer);
        return;
      }

      // otherwise add the answer to the list
      addAnswer(answer);

      if (!answer.correct) {
        toast.error(answer.solution);
        return;
      }

      if (mode === "classic") {
        addRewardTime(3); // 3 seconds per correct answer
      }

      toast.success(answer.solution);
      blastConfetti();
    },
    [
      mode,
      answers,
      addAnswer,
      game.words.length,
      addRewardTime,
      blastConfetti,
      endGame,
    ],
  );

  const [theme] = useTheme();
  const isDefaultTheme = useMemo(
    () =>
      match(theme)
        .with("dark", "knoword", () => true)
        .otherwise(() => false),
    [theme],
  );

  return (
    <>
      <div className={clsx("grid p-4 md:p-6", isDefaultTheme && "bg-base-200")}>
        <div
          className={clsx(
            "relative flex items-center justify-center rounded border text-center shadow backdrop-blur",
            isDefaultTheme ? "bg-base-100" : "bg-base-100/60",
          )}
        >
          <GameClue size="lg" word={word} isImageMatch={game.mode === "imcq"} />
        </div>
      </div>
      <div className="relative bg-base-100/60 backdrop-blur">
        {match(mode)
          .with("classic", () => (
            <Classic
              key={word.id}
              word={word}
              onAnswer={handleAnswer}
              isDisabled={isDisabled}
            />
          ))
          .with("mcq", "imcq", () => (
            <Choices
              word={word}
              onAnswer={handleAnswer}
              isDisabled={isDisabled}
            />
          ))
          .exhaustive()}
      </div>
    </>
  );
};

export default memo(QuestionAnswer);
